import React, { forwardRef, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import getStyled from "../../hooks/getStyled";
import { Colors } from "../../constants/colors";

// Material-UI Components
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";

// Core Components
import SFHaloIconWrapper from "../icons/halo";
import {
  SFErrorIcon,
  SFInfoIconFilled,
  SFCheckCircleIcon,
  SFWarningIcon,
  SFCloseIcon,
} from "../icons";
import { SFStack } from "../grid";
import SFTypography from "../typography";
import SFIconButton from "../button/iconButton";

const { styled } = getStyled();

const SFTypographyStyled = styled(SFTypography)({
  // Make the text overflow with ellipsis after 3 lines
  overflow: "hidden",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 3,
});

const LinkStyled = styled(Link)({
  textTransform: "uppercase",
});

export const SFToastAction = ({ label, onClick }) => {
  const UNDERLINE_HOVER = "hover"; // workaround for 'disallow literal string' eslint rule
  return (
    <LinkStyled
      color={Colors.BLUE500}
      component="button"
      underline={UNDERLINE_HOVER}
      variant="buttonDefault"
      onClick={onClick}
    >
      {label}
    </LinkStyled>
  );
};

SFToastAction.propTypes = {
  /** The label of the action button. */
  label: PropTypes.string,
  /** The function to call when the action button is clicked. */
  onClick: PropTypes.func,
};

const SFToast = forwardRef(
  ({ actionProps = {}, message, onClose, onView, severity = "info" }, ref) => {
    const typographyRef = useRef(null);
    const [isOverflowed, setIsOverflowed] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
      const typographyEl = typographyRef.current;
      if (typographyEl) {
        setIsOverflowed(typographyEl.scrollHeight > typographyEl.offsetHeight);
      }
    }, [message]);

    return (
      <Alert
        iconMapping={{
          error: (
            <SFHaloIconWrapper variant="red" ignoreIcon>
              <SFErrorIcon color="red400" />
            </SFHaloIconWrapper>
          ),
          info: (
            <SFHaloIconWrapper variant="blue" ignoreIcon>
              <SFInfoIconFilled color="blue400" />
            </SFHaloIconWrapper>
          ),
          success: (
            <SFHaloIconWrapper variant="green" ignoreIcon>
              <SFCheckCircleIcon color="green300" />
            </SFHaloIconWrapper>
          ),
          warning: (
            <SFHaloIconWrapper variant="orange" ignoreIcon>
              <SFWarningIcon color="orange300" rounded />
            </SFHaloIconWrapper>
          ),
        }}
        severity={severity}
        ref={ref}
      >
        <SFStack
          direction="row"
          spacing={2.5}
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <SFTypographyStyled variant="body1" ref={typographyRef}>
            {message}
          </SFTypographyStyled>
          <SFStack direction="row" spacing={2}>
            {Object.keys(actionProps).length > 0 ? (
              // If actionProps is provided, display an action button (even if the message is overflowed)
              <SFToastAction
                label={actionProps.label}
                onClick={() => {
                  actionProps.onClick();
                  onClose();
                }}
              />
            ) : (
              isOverflowed && (
                // Else, if the message is overflowed, display a "VIEW" action button
                <SFToastAction
                  label={t("components.toast.actions.view")}
                  onClick={() => {
                    onView();
                    onClose();
                  }}
                />
              )
            )}
            <SFIconButton
              onClick={onClose}
              size="small"
              sx={(theme) => ({
                backgroundColor: theme.palette.grey100.main,
              })}
            >
              <SFCloseIcon color="grey500" size="small" />
            </SFIconButton>
          </SFStack>
        </SFStack>
      </Alert>
    );
  }
);

SFToast.propTypes = {
  /** The props for the action button. If not provided, the action button will not be displayed:
   * - `label`: The label of the action button.
   * - `onClick`: Callback fired when the action button is clicked.
   */
  actionProps: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  /** The message to display in the toast. */
  message: PropTypes.string,
  /** Callback fired when the close button is clicked. */
  onClose: PropTypes.func,
  /** Callback fired when the view action button is clicked (only displayed if the message exceeds 3 lines and no `actionProps` are provided). */
  onView: PropTypes.func,
  /** The severity of the toast (determines the displayed icon). */
  severity: PropTypes.oneOf(["error", "info", "success", "warning"]),
};

export default SFToast;
